import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { TRANSLATIONS } from "./translations";
import { SUPPORTED_LANGUAGE_CODES } from "./languages";

export const DEFAULT_LANGUAGE = "en";
export const SUPPORTED_LANGUAGES = SUPPORTED_LANGUAGE_CODES;
const STORAGE_KEY = "imago_language";

function normalizeLanguage(value) {
  const language = String(value || "").trim().toLowerCase().split("-")[0];
  return SUPPORTED_LANGUAGES.includes(language) ? language : DEFAULT_LANGUAGE;
}

function getInitialLanguage() {
  if (typeof window === "undefined") return DEFAULT_LANGUAGE;

  const urlLanguage = new URLSearchParams(window.location.search).get("lang");
  if (urlLanguage && SUPPORTED_LANGUAGES.includes(normalizeLanguage(urlLanguage))) {
    return normalizeLanguage(urlLanguage);
  }

  const savedLanguage = window.localStorage.getItem(STORAGE_KEY);
  if (savedLanguage && SUPPORTED_LANGUAGES.includes(normalizeLanguage(savedLanguage))) {
    return normalizeLanguage(savedLanguage);
  }

  return normalizeLanguage(window.navigator.language);
}

export function translateText(language, text) {
  if (typeof text !== "string") return text;
  return TRANSLATIONS[language]?.[text] || text;
}

export function localizeValue(value, language) {
  if (typeof value === "string") return translateText(language, value);
  if (Array.isArray(value)) return value.map((item) => localizeValue(item, language));
  if (value && typeof value === "object") {
    return Object.fromEntries(
      Object.entries(value).map(([key, item]) => [key, localizeValue(item, language)]),
    );
  }
  return value;
}

export function getLocalizedField(record, field, language) {
  if (!record) return undefined;
  const languageSuffix = language.charAt(0).toUpperCase() + language.slice(1);
  const translation = Array.isArray(record.translations)
    ? record.translations.find((item) => item?.language === language)
    : record.translations?.[language];
  const candidates = [
    translation?.[field],
    record[`${field}_${language}`],
    record[`${field}${languageSuffix}`],
    record[field],
  ];
  const localized = candidates.find((candidate) => {
    if (typeof candidate === "string") return candidate.trim().length > 0;
    if (Array.isArray(candidate)) return candidate.length > 0;
    return candidate !== undefined && candidate !== null;
  });
  return localizeValue(localized, language);
}

const I18nContext = createContext(null);

export function I18nProvider({ children }) {
  const [language, setLanguageState] = useState(getInitialLanguage);

  const setLanguage = useCallback((nextLanguage) => {
    setLanguageState(normalizeLanguage(nextLanguage));
  }, []);

  useEffect(() => {
    document.documentElement.lang = language;
    window.localStorage.setItem(STORAGE_KEY, language);
  }, [language]);

  const t = useCallback((text) => translateText(language, text), [language]);
  const field = useCallback(
    (record, fieldName) => getLocalizedField(record, fieldName, language),
    [language],
  );

  const value = useMemo(
    () => ({ language, setLanguage, t, field }),
    [field, language, setLanguage, t],
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n() {
  const context = useContext(I18nContext);
  if (!context) throw new Error("useI18n must be used inside I18nProvider");
  return context;
}
